import { useCallback, useEffect, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import {
  deleteEntityDocument,
  downloadEntityDocument,
  EntityDocument,
  getEntityDocuments,
  uploadEntityDocument,
} from 'api/bitedb/entities/entityDocuments.service';

export const LIBRARY_MAX_FILE_SIZE = 25 * 1024 * 1024;
export const LIBRARY_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'csv',
  'txt',
  'png',
  'jpg',
  'jpeg',
];

export interface LibraryUpload {
  key: string;
  fileName: string;
  progress: number;
}

/** An entity record's Library: the files kept against it. */
export interface EntityLibrary {
  available: boolean;
  documents: EntityDocument[];
  loading: boolean;
  failed: boolean;
  uploads: LibraryUpload[];
  deletingId: string | null;
  reload: () => void;
  upload: (files: File[]) => Promise<void>;
  download: (document: EntityDocument) => Promise<void>;
  remove: (documentId: string) => Promise<boolean>;
}

const fileKey = (file: File): string =>
  `${file.name}-${file.size}-${file.lastModified}`;

const acceptable = (file: File): boolean => {
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  if (!LIBRARY_EXTENSIONS.includes(extension)) {
    enqueueSnackbar(`${file.name} is not a supported file type.`, {
      variant: 'warning',
    });
    return false;
  }
  if (file.size > LIBRARY_MAX_FILE_SIZE) {
    enqueueSnackbar(`${file.name} is larger than 25 MB.`, {
      variant: 'warning',
    });
    return false;
  }
  return true;
};

/**
 * Loads the record's Library with the record, so its count is right before
 * anyone opens it, and keeps it current through uploads, deletes and filed
 * investigation reports.
 */
export const useEntityLibrary = (
  databaseId?: string | null,
  databaseEntityId?: string | null,
): EntityLibrary => {
  const available = Boolean(databaseId && databaseEntityId);
  const [documents, setDocuments] = useState<EntityDocument[]>([]);
  const [loading, setLoading] = useState(available);
  const [failed, setFailed] = useState(false);
  const [uploads, setUploads] = useState<LibraryUpload[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const latestLoad = useRef(0);

  const load = useCallback(async () => {
    const thisLoad = latestLoad.current + 1;
    latestLoad.current = thisLoad;
    if (!databaseId || !databaseEntityId) {
      setDocuments([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setFailed(false);
    try {
      const result = await getEntityDocuments(databaseId, databaseEntityId);
      if (thisLoad === latestLoad.current) setDocuments(result);
    } catch {
      if (thisLoad === latestLoad.current) setFailed(true);
    } finally {
      if (thisLoad === latestLoad.current) setLoading(false);
    }
  }, [databaseEntityId, databaseId]);

  useEffect(() => {
    setDocuments([]);
    load();
  }, [load]);

  const upload = useCallback(
    async (files: File[]) => {
      if (!databaseId || !databaseEntityId) return;
      for (const file of files.filter(acceptable)) {
        const key = fileKey(file);
        setUploads((current) => [
          ...current,
          { key: key, fileName: file.name, progress: 0 },
        ]);
        try {
          const next = await uploadEntityDocument(
            databaseId,
            databaseEntityId,
            file,
            (progress) =>
              setUploads((current) =>
                current.map((item) =>
                  item.key === key ? { ...item, progress: progress } : item,
                ),
              ),
          );
          setDocuments(next);
          enqueueSnackbar(`${file.name} uploaded.`, { variant: 'success' });
        } catch {
          enqueueSnackbar(`Could not upload ${file.name}.`, {
            variant: 'error',
          });
        } finally {
          setUploads((current) => current.filter((item) => item.key !== key));
        }
      }
    },
    [databaseEntityId, databaseId],
  );

  const download = useCallback(
    async (document: EntityDocument) => {
      if (!databaseId) return;
      try {
        await downloadEntityDocument(databaseId, document);
      } catch {
        enqueueSnackbar('Could not download this file.', { variant: 'error' });
      }
    },
    [databaseId],
  );

  const remove = useCallback(
    async (documentId: string) => {
      if (!databaseId || deletingId) return false;
      setDeletingId(documentId);
      try {
        await deleteEntityDocument(databaseId, documentId);
        setDocuments((current) =>
          current.filter((document) => document.id !== documentId),
        );
        enqueueSnackbar('File removed from the Library.', {
          variant: 'success',
        });
        return true;
      } catch {
        enqueueSnackbar('Could not remove this file.', { variant: 'error' });
        return false;
      } finally {
        setDeletingId(null);
      }
    },
    [databaseId, deletingId],
  );

  return {
    available: available,
    documents: documents,
    loading: loading,
    failed: failed,
    uploads: uploads,
    deletingId: deletingId,
    reload: load,
    upload: upload,
    download: download,
    remove: remove,
  };
};
